/**
 * Phone number formatting utilities for SMS providers
 * All major SMS providers (Firebase, Twilio, AWS SNS) require E.164 format
 */

export interface PhoneFormatOptions {
    defaultCountryCode?: string;
    allowedCountries?: string[];
}

/**
 * Convert phone number to E.164 format
 * Supports Thai numbers by default, with configurable country codes
 * 
 * @param phoneNumber Raw phone number input
 * @param options Configuration options
 * @returns E.164 formatted phone number (+66xxxxxxxxx)
 * @throws Error if phone number format is invalid
 */
export function toE164(phoneNumber: string, options: PhoneFormatOptions = {}): string {
    const { defaultCountryCode = '66', allowedCountries = ['66'] } = options;

    if (!phoneNumber?.trim()) {
        throw new Error('กรุณากรอกหมายเลขโทรศัพท์');
    }

    // Remove all non-digit characters except '+'
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');

    if (!cleaned) {
        throw new Error('หมายเลขโทรศัพท์ไม่ถูกต้อง');
    }

    // Extract digits only for validation
    const digits = cleaned.replace(/\D/g, '');

    // Case 1: Already in E.164 format (+66xxxxxxxxx)
    if (cleaned.startsWith('+')) {
        const countryCodeMatch = cleaned.match(/^\+(\d{1,3})/);
        if (countryCodeMatch) {
            const countryCode = countryCodeMatch[1];

            // Validate country code if restrictions are set
            if (allowedCountries.length > 0 && !allowedCountries.includes(countryCode)) {
                throw new Error(`รองรับเฉพาะประเทศ: ${allowedCountries.map(cc => `+${cc}`).join(', ')}`);
            }

            // Validate Thai number length
            if (countryCode === '66') {
                const nationalNumber = digits.slice(2); // Remove country code
                if (nationalNumber.length !== 9) {
                    throw new Error('หมายเลขโทรศัพท์ไทยต้องมี 9 หลักหลังรหัสประเทศ (+66xxxxxxxxx)');
                }
                if (!nationalNumber.match(/^[689]/)) {
                    throw new Error('หมายเลขโทรศัพท์ไทยต้องขึ้นต้นด้วย 6, 8, หรือ 9');
                }
            }

            return cleaned;
        }
        throw new Error('รูปแบบรหัสประเทศไม่ถูกต้อง');
    }

    // Case 2: Thai number starting with 0 (081xxxxxxx -> +66xxxxxxxx)
    if (digits.startsWith('0') && digits.length === 10) {
        const nationalNumber = digits.slice(1); // Remove leading 0
        if (!nationalNumber.match(/^[689]/)) {
            throw new Error('หมายเลขโทรศัพท์ไทยต้องขึ้นต้นด้วย 06, 08, หรือ 09');
        }
        return `+${defaultCountryCode}${nationalNumber}`;
    }

    // Case 3: Country code without + (66xxxxxxxxx -> +66xxxxxxxxx)
    if (digits.startsWith(defaultCountryCode) && digits.length === 11) {
        const nationalNumber = digits.slice(2);
        if (!nationalNumber.match(/^[689]/)) {
            throw new Error('หมายเลขโทรศัพท์ไทยต้องขึ้นต้นด้วย 6, 8, หรือ 9');
        }
        return `+${digits}`;
    }

    // Case 4: 9-digit national number (8xxxxxxxx -> +668xxxxxxxx)
    if (digits.length === 9 && digits.match(/^[689]/)) {
        return `+${defaultCountryCode}${digits}`;
    }

    // If none of the patterns match, provide helpful error
    throw new Error(
        'รูปแบบหมายเลขโทรศัพท์ไม่ถูกต้อง\n' +
        'รูปแบบที่รองรับ:\n' +
        '• +66xxxxxxxxx (E.164)\n' +
        '• 0xxxxxxxxx (10 หลัก)\n' +
        '• 66xxxxxxxxx (11 หลัก)\n' +
        '• xxxxxxxxx (9 หลัก)'
    );
}

/**
 * Validate if phone number is in valid E.164 format
 * @param phoneNumber Phone number to validate
 * @returns true if valid E.164 format
 */
export function isValidE164(phoneNumber: string): boolean {
    if (!phoneNumber) return false;

    // Basic E.164 validation: +[1-3 digits country code][4-14 digits]
    const e164Regex = /^\+[1-9]\d{6,14}$/;
    return e164Regex.test(phoneNumber);
}

/**
 * Format phone number for display (with spaces)
 * @param phoneNumber E.164 formatted phone number
 * @returns Formatted display string
 */
export function formatPhoneDisplay(phoneNumber: string): string {
    if (!phoneNumber) return '';

    // Thai number formatting: +66 8 1234 5678
    if (phoneNumber.startsWith('+66')) {
        const national = phoneNumber.slice(3);
        if (national.length === 9) {
            return `+66 ${national[0]} ${national.slice(1, 5)} ${national.slice(5)}`;
        }
    }

    // Generic formatting: +XX XXX XXX XXXX
    const match = phoneNumber.match(/^\+(\d{1,3})(\d{3})(\d{3})(\d+)$/);
    if (match) {
        return `+${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
    }

    return phoneNumber;
}

/**
 * Get country info from E.164 phone number
 * @param phoneNumber E.164 formatted phone number
 * @returns Country information
 */
export function getPhoneCountryInfo(phoneNumber: string): { code: string; name: string } | null {
    if (!phoneNumber?.startsWith('+')) return null;

    const countryMappings: Record<string, string> = {
        '66': 'Thailand',
        '1': 'United States/Canada',
        '44': 'United Kingdom',
        '81': 'Japan',
        '86': 'China',
        '65': 'Singapore',
        '60': 'Malaysia',
        '62': 'Indonesia',
        '84': 'Vietnam',
        '63': 'Philippines'
    };

    // Try different country code lengths (1-3 digits)
    for (let len = 1; len <= 3; len++) {
        const code = phoneNumber.slice(1, 1 + len);
        if (countryMappings[code]) {
            return { code, name: countryMappings[code] };
        }
    }

    return null;
}

/**
 * Thai-specific phone number formatter (convenience function)
 * @param phoneNumber Raw phone number input
 * @returns E.164 formatted Thai phone number
 */
export function toE164Thai(phoneNumber: string): string {
    return toE164(phoneNumber, {
        defaultCountryCode: '66',
        allowedCountries: ['66']
    });
}
